import { useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { User } from '@microsoft/microsoft-graph-types';
import { graphQueries } from '../../api/graph.api';

export type SecondaryOwners = {
  /** The owners that resolved to a user. One that is deleted, is not a user (a group) or failed to load is left out. */
  users: User[];
  /** Every lookup has finished without an error, so `users` is the whole answer rather than what has arrived so far. */
  isSettled: boolean;
};

/**
 * Technical and business owners are stored as bare ids; this resolves them to users. The owner tables and
 * the compliance count both read it, so the lookups are shared through the query cache.
 * Nothing is looked up when `enabled` is false.
 */
const useSecondaryOwners = (ownerIds: string[], enabled = true): SecondaryOwners => {
  const { warnings, ...owners } = useQueries({
    queries: enabled ? ownerIds.map((ownerId) => graphQueries.getUserById(ownerId)) : [],
    combine: (results) => ({
      users: results.flatMap((result) => (result.data ? [result.data] : [])),
      isSettled: results.every((result) => !result.isPending && !result.isError),
      warnings: results.flatMap((result, index) => {
        if (result.isError) return [`Error fetching user ${ownerIds[index]}: ${result.error.message}`];
        if (!result.isPending && !result.data) return [`Skipping invalid or deleted user ${ownerIds[index]}`];
        return [];
      }),
    }),
  });

  // Logged from an effect: `combine` runs on every render, and the same owner would be reported each time.
  useEffect(() => warnings.forEach((warning) => console.warn(warning)), [warnings]);

  return owners;
};

export default useSecondaryOwners;
